import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type EstadoLogistico = "En Origen" | "En Tránsito" | "En Puerto" | "Nacionalizado";

export const ESTADOS_LOGISTICOS: EstadoLogistico[] = [
  "En Origen",
  "En Tránsito",
  "En Puerto",
  "Nacionalizado",
];

export type ModoTransporte = "Marítimo" | "Aéreo" | "Terrestre";

export const MODOS_TRANSPORTE: ModoTransporte[] = ["Marítimo", "Aéreo", "Terrestre"];

export interface Embarque {
  id: string;
  user_id: string;
  org_id: string;
  numero_do: string;
  proveedor: string | null;
  proveedor_id: string | null;
  estado_logistico: EstadoLogistico;
  fecha_llegada_estimada: string | null;
  created_at: string;
  document_count?: number;
  tracking_number?: string | null;
  shipping_line?: string | null;
  tracking_history?: any[] | null;
  trm_aduana?: number | null;
  liquidado?: boolean;
  cliente_id?: string | null;
  peso_bruto_kg?: number | null;
  volumen_cbm?: number | null;
  modo_transporte?: ModoTransporte | null;
}

export function useEmbarques() {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["embarques", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("embarques")
        .select("*, registro_documentos(count)")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((e: any) => ({
        ...e,
        document_count: e.registro_documentos?.[0]?.count ?? 0,
      })) as Embarque[];
    },
    enabled: !!user && !!orgId,
  });
}

export function useUpdateEmbarqueEstado() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, estado }: { id: string; estado: EstadoLogistico }) => {
      const { error } = await supabase
        .from("embarques")
        .update({ estado_logistico: estado })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["embarques"] });
    },
  });
}

export function useCreateEmbarque() {
  const queryClient = useQueryClient();
  const { user, orgId } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      numero_do: string;
      proveedor?: string;
      proveedor_id?: string;
      cliente_id: string;
      fecha_llegada_estimada?: string;
    }) => {
      if (!orgId) throw new Error("No organization context");
      if (!data.cliente_id) throw new Error("El cliente es obligatorio");
      const { error } = await supabase.from("embarques").insert({
        ...data,
        user_id: user!.id,
        org_id: orgId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["embarques"] });
    },
  });
}

export function useEmbarqueDocuments(embarqueId: string | null) {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["embarque-documents", orgId, embarqueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("registro_documentos")
        .select("*")
        .eq("embarque_id", embarqueId!)
        .eq("org_id", orgId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!orgId && !!embarqueId,
  });
}
